import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function EditTripDialog({ trip, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: trip.name || "",
    destination: trip.destination || "",
    start_date: trip.start_date || "",
    end_date: trip.end_date || "",
    entity: trip.entity || "",
    billable: !!trip.billable,
  });
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setEntities((data.entities?.entities || []).map((e) => e.name).filter(Boolean));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: form.name || null,
        destination: form.destination || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        entity: form.entity,
        billable: form.billable,
      };
      await api.patch(`/trips/${trip.id}`, body);
      toast.success("Trip updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-sand-300" data-testid="edit-trip-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            Edit trip
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="label-tag block mb-1">Name</label>
            <input className="swiss-input text-sm" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-trip-name" />
          </div>
          <div>
            <label className="label-tag block mb-1">Destination</label>
            <input className="swiss-input text-sm" value={form.destination}
              onChange={(e) => setForm({ ...form, destination: e.target.value })}
              data-testid="edit-trip-destination" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-tag block mb-1">Start (YYYY-MM-DD)</label>
              <input className="swiss-input text-sm font-mono" value={form.start_date}
                placeholder="2026-07-08"
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="edit-trip-start" />
            </div>
            <div>
              <label className="label-tag block mb-1">End (YYYY-MM-DD)</label>
              <input className="swiss-input text-sm font-mono" value={form.end_date}
                placeholder="2026-07-15"
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="edit-trip-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="label-tag block mb-1">Entity</label>
              <select className="swiss-input text-sm font-mono" value={form.entity}
                onChange={(e) => setForm({ ...form, entity: e.target.value })}
                data-testid="edit-trip-entity">
                <option value="">Unassigned</option>
                {entities.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pb-2.5">
              <input type="checkbox" checked={form.billable}
                onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                className="accent-ember" data-testid="edit-trip-billable" />
              Billable
            </label>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="edit-trip-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy} className="btn-swiss btn-primary text-sm" data-testid="edit-trip-save">
            {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
